/**
 * This plugin adds a keymap handler to change the node type.
 *
 * Browser and OS differences make it difficult to have consistent keybindings
 * for this. The general principle is that it binds a modifier and a number key
 * to activate the bound node for the current selection. Here's an overview:
 *
 * - On PC and Linux: these map to ctrl + digit
 * - On Mac: these map to cmd + digit
 * - Shift can be used as a modifier for some keys
 * - Safari comandeers the cmd + digit shortcut for switching tabs in a way
 *   that cannot be overridden, so we use ctrl + digit for these
 * - PCs use the ctrl + shift modifier to switch keyboard layouts, so the user
 *   needs to disable this explicitly if they want to use a combo that makes
 *   use of those keys
 *
 * This also sets up a global event listener for the modifier + digit to prevent
 * switching to another tab, so if you do cmd + 7 in a screenplay, this won't
 * bump you out of the current tab, even though there isn't (currently) a
 * command mapped to that combo.
 */

import { keydownHandler } from 'prosemirror-keymap'
import { NodeType } from 'prosemirror-model'
import { Command, EditorState, Plugin, PluginKey } from 'prosemirror-state'

import { schema } from '@showrunner/prose-schemas'

import env from '#choo-app/lib/env.js'
import { Keys } from '#hooks/index'
import { buildShortcutTip } from '#hooks/useShortcuts/helpers'
import { setEditorBlockType } from '#util/index'

import { PluginFactory } from './types'

interface Browser {
  safari: boolean
  mac: boolean
}

export interface ElementKeyConfig {
  node: NodeType
  shortcut: string
  command: Command
  displayName: string
}

type Digit = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9
export type Binding = [Digit, NodeType, 'additional-modifier'?]

const screenplayBindings: Binding[] = [
  [0, schema.nodes.general],
  [1, schema.nodes.sceneHeading],
  [2, schema.nodes.action],
  [3, schema.nodes.character],
  [4, schema.nodes.parenthetical],
  [5, schema.nodes.dialogue],
  [6, schema.nodes.transition],
  [9, schema.nodes.new_act],
  [9, schema.nodes.end_of_act, 'additional-modifier'],
]

const varietyBindings: Binding[] = [
  [0, schema.nodes.general],
  [1, schema.nodes.slug],
  [2, schema.nodes.bracket],
  [3, schema.nodes.character],
  [4, schema.nodes.parenthetical],
  [5, schema.nodes.dialogue],
  [9, schema.nodes.new_act],
  [9, schema.nodes.end_of_act, 'additional-modifier'],
]

const bindingsByType = {
  screenplay: screenplayBindings,
  classic: varietyBindings,
  variety: varietyBindings,
} as const

// Used to prevent switching to another tab, even if the editor is not focused
const ignoreKeydown = keydownHandler({
  'mod-1': () => true,
  'mod-2': () => true,
  'mod-3': () => true,
  'mod-4': () => true,
  'mod-5': () => true,
  'mod-6': () => true,
  'mod-7': () => true,
  'mod-8': () => true,
  'mod-9': () => true,
  'mod-0': () => true,
})

export const elementPickerKeymapPlugin: PluginFactory = ({ script }) => {
  return createPlugin({
    bindings: bindingsByType[script.type],
    browser: env.browser,
  })
}

const pluginKey = new PluginKey('element-picker-keymap')

interface PluginOptions {
  browser: Browser
  bindings: Binding[]
}

export function createPlugin(options: PluginOptions) {
  const keys: ElementKeyConfig[] = []
  const bindings: Record<string, Command> = {}

  for (const [digit, node, withModifier] of options.bindings) {
    const combo = buildCombo(options.browser, digit, Boolean(withModifier))
    const command = setEditorBlockType(node.name)
    const shortcut = buildShortcutTip(combo)
    const displayName = node.spec.displayName ?? node.name

    bindings[combo.join('-')] = command
    keys.push({ node, shortcut, command, displayName })
  }

  const handleKeyDown = keydownHandler(bindings)

  return new Plugin({
    key: pluginKey,
    keys,
    props: { handleKeyDown },
    view(view) {
      function preventTabSwitch(event: KeyboardEvent) {
        if (ignoreKeydown(view, event)) event.preventDefault()
      }

      document.addEventListener('keydown', preventTabSwitch)

      return {
        destroy() {
          document.removeEventListener('keydown', preventTabSwitch)
        },
      }
    },
  })
}

function buildCombo(browser: Browser, digit: Digit, extraModifier = false) {
  const combo = [browser.safari ? Keys.CTRL : Keys.MOD]

  if (extraModifier) {
    if (browser.mac && !browser.safari) {
      combo.push(Keys.CTRL)
    } else {
      combo.push(Keys.SHIFT)
    }
  }

  combo.push(String(digit))

  return combo
}

/**
 * Returns the list of bound keys and the commands to invoke them
 */
export function elementPickerKeys(state: EditorState): ElementKeyConfig[] {
  return pluginKey.get(state)?.spec.keys ?? []
}
